import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import ParticleField from "@/components/ParticleField";
import SEO from "@/components/SEO";

export default function AdminLogin() {
  const { login, user, ready } = useAuth();
  const { content } = useContent();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (ready && user) navigate("/admin/dashboard", { replace: true });
  }, [ready, user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full rounded-lg border border-white/10 bg-ink-900 px-4 py-3 font-body text-white placeholder:text-white/30 outline-none transition-colors focus:border-gold";

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-ink-900 px-6">
      <SEO title="Admin — ImJove Tech" description="ImJove Tech content management." />
      <ParticleField className="absolute inset-0 z-0" density={35} />
      <div className="radial-fade absolute inset-0" />
      <div className="glass relative z-10 w-full max-w-md rounded-2xl p-8">
        <div className="flex items-center gap-3">
          <img src={content.logo_url} alt="ImJove Tech" className="h-10 w-10 object-contain" />
          <div>
            <p className="font-display text-sm font-semibold uppercase tracking-brand text-white">ImJove Tech</p>
            <p className="font-mono2 text-[10px] uppercase tracking-widest text-gold-light">Control Center</p>
          </div>
        </div>
        <h1 className="mt-8 font-display text-3xl font-bold tracking-tighter text-white">Admin Access</h1>
        <p className="mt-2 font-body text-sm text-white/45">Sign in to manage the ImJove ecosystem.</p>
        <form onSubmit={submit} className="mt-7 space-y-4" data-testid="admin-login-form">
          <input data-testid="admin-email" type="email" className={inputCls} placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input data-testid="admin-password" type="password" className={inputCls} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
          <button type="submit" disabled={busy} data-testid="admin-login-submit" className="btn-gold inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 font-mono2 text-[12px] font-semibold uppercase tracking-widest disabled:opacity-60">
            <Lock size={14} /> {busy ? "Signing in…" : "Sign In"} <ArrowRight size={14} />
          </button>
        </form>
      </div>
    </div>
  );
}
